import ApolloClient from "apollo-client";
import axios from "axios";
import { InMemoryCache } from "apollo-cache-inmemory";
import { ApolloLink } from "apollo-link";
import { HttpLink } from "apollo-link-http";
import { useAuthToken } from "./auth"; // Updated auth hook
import { onError } from "apollo-link-error";

// Auth middleware to add the Authorization header
const authMiddleware = (authToken) =>
  new ApolloLink((operation, forward) => {
    if (authToken) {
      operation.setContext({
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
    }
    return forward(operation);
  });

// Axios instance with base URL and global headers
const httpClient = axios.create({
  baseURL: process.env.REACT_APP_GRAPH_ENDPOINT,
});

httpClient.interceptors.request.use((config) => {
  config.headers.Authorization = `Bearer ${process.env.REACT_APP_MAGIC_KEY}`;
  return config;
});

export const useAppApolloClient = () => {
  const [authToken, setAuthToken] = useAuthToken(); // Updated to use localStorage fallback

  // Function to fetch a new session token
  const getToken = async () => {
    try {
      const response = await httpClient.post(
        process.env.REACT_APP_GRAPH_ENDPOINT,
        {
          query: `mutation generateSessionToken($personId: String) {
          generateSessionToken(personId: $personId) {
            person {
              sessionToken
              sessionTokenExpiry
            }
          }
        }`,
          variables: {
            personId: localStorage.getItem("hashId"),
          },
        }
      );

      const person = response.data.data.generateSessionToken.person;
      const exp = new Date(person?.sessionTokenExpiry);
      setAuthToken(person?.sessionToken, exp);

      // Optional: Trigger any follow-up actions after getting the new token
      // setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error("Failed to fetch session token", error);
    }
  };

  // Error handling link to manage GraphQL and network errors
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      console.error("GraphQL errors:", graphQLErrors);
    }

    if (networkError) {
      if (
        networkError.result?.error === "Authentication Failed" ||
        (networkError.result?.error ===
          "Request cannot be completed, transcript access denied." &&
          localStorage.getItem("hashId"))
      ) {
        console.warn("Authentication error, fetching new token...");
        getToken();
      }
    }
  });

  // Apollo Client configuration with links
  const client = new ApolloClient({
    link: ApolloLink.from([
      errorLink, // Handles errors and triggers token refresh
      authMiddleware(authToken || process.env.REACT_APP_MAGIC_KEY), // Adds Authorization header with token from useAuthToken hook
      new HttpLink({
        uri: process.env.REACT_APP_GRAPH_ENDPOINT,
        credentials: "include", // Include credentials for CORS
      }),
    ]),
    cache: new InMemoryCache(),
  });

  return client;
};
